import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  useToast,
} from '@/components/base';

type LocalAITask =
  | 'chat'
  | 'summarization'
  | 'extraction'
  | 'classification'
  | 'generation';

type JobListItem = {
  id: string;
  task: LocalAITask;
  status: string;
  risk_level?: string;
  client_id?: string | null;
  case_id?: string | null;
  procedure_id?: string | null;
  model?: string | null;
  error_text?: string | null;
  created_at?: string;
  updated_at?: string;
};

type JobDetail = {
  id: string;
  task: LocalAITask;
  status: string;
  result?: unknown;
  error?: string | null;
  model?: string | null;
  completedAt?: string | null;
};

const taskLabels:
  Record<
    LocalAITask,
    string
  > = {
  chat:
    'Assistente juridico',
  summarization:
    'Resumir',
  extraction:
    'Extrair dados',
  classification:
    'Classificar',
  generation:
    'Gerar minuta',
};

const readApiError = async (
  response: Response
) => {
  try {
    const data =
      await response
        .json() as {
          error?: string;
        };

    return data.error ||
      `HTTP ${response.status}`;
  }
  catch {
    return `HTTP ${response.status}`;
  }
};

const formatResult = (
  value: unknown
) => {
  if (
    value === null ||
    value === undefined
  ) {
    return '';
  }

  if (
    typeof value ===
      'string'
  ) {
    return value;
  }

  try {
    return JSON.stringify(
      value,
      null,
      2
    );
  }
  catch {
    return String(value);
  }
};

export default function LocalAIAssistant() {
  const {
    addToast,
  } = useToast();

  const [
    task,
    setTask,
  ] =
    useState<
      LocalAITask
    >(
      'chat'
    );

  const [
    prompt,
    setPrompt,
  ] =
    useState('');

  const [
    clientId,
    setClientId,
  ] =
    useState('');

  const [
    caseId,
    setCaseId,
  ] =
    useState('');

  const [
    procedureId,
    setProcedureId,
  ] =
    useState('');

  const [
    submitting,
    setSubmitting,
  ] =
    useState(false);

  const [
    loading,
    setLoading,
  ] =
    useState(false);

  const [
    jobs,
    setJobs,
  ] =
    useState<
      JobListItem[]
    >([]);

  const [
    detail,
    setDetail,
  ] =
    useState<
      JobDetail |
      null
    >(null);

  const loadJobs =
    useCallback(
      async () => {
        setLoading(
          true
        );

        try {
          const response =
            await fetch(
              '/api/ai/jobs?limit=12',
              {
                credentials:
                  'same-origin',
              }
            );

          if (!response.ok) {
            throw new Error(
              await readApiError(
                response
              )
            );
          }

          const data =
            await response
              .json() as {
                jobs?: JobListItem[];
              };

          setJobs(
            Array.isArray(
              data.jobs
            )
              ? data.jobs
              : []
          );
        }
        catch (
          error
        ) {
          addToast(
            error instanceof Error
              ? error.message
              : 'Nao foi possivel carregar a fila de IA.',
            'error'
          );
        }
        finally {
          setLoading(
            false
          );
        }
      },
      [
        addToast,
      ]
    );

  useEffect(
    () => {
      void loadJobs();
    },
    [
      loadJobs,
    ]
  );

  const submit =
    async () => {
      const text =
        prompt.trim();

      if (!text) {
        addToast(
          'Informe a tarefa para o assistente.',
          'error'
        );
        return;
      }

      setSubmitting(
        true
      );

      try {
        const response =
          await fetch(
            '/api/ai/jobs',
            {
              method:
                'POST',

              credentials:
                'same-origin',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify({
                  task,
                  prompt:
                    text,

                  clientId:
                    clientId.trim() ||
                    null,

                  caseId:
                    caseId.trim() ||
                    null,

                  procedureId:
                    procedureId.trim() ||
                    null,

                  riskLevel:
                    task ===
                      'generation'
                      ? 'A3'
                      : 'A2',
                }),
            }
          );

        if (!response.ok) {
          throw new Error(
            await readApiError(
              response
            )
          );
        }

        const data =
          await response
            .json() as {
              job?: {
                id?: string;
              };
            };

        addToast(
          data.job?.id
            ? 'Tarefa enviada para a IA local.'
            : 'Tarefa criada.',
          'success'
        );

        setPrompt('');
        setDetail(
          null
        );

        await loadJobs();
      }
      catch (
        error
      ) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Nao foi possivel criar a tarefa de IA.',
          'error'
        );
      }
      finally {
        setSubmitting(
          false
        );
      }
    };

  const openJob =
    async (
      id: string
    ) => {
      try {
        const response =
          await fetch(
            `/api/ai/jobs/${encodeURIComponent(
              id
            )}`,
            {
              credentials:
                'same-origin',
            }
          );

        if (!response.ok) {
          throw new Error(
            await readApiError(
              response
            )
          );
        }

        const data =
          await response
            .json() as {
              job?: JobDetail;
            };

        setDetail(
          data.job ??
          null
        );
      }
      catch (
        error
      ) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Nao foi possivel abrir o resultado.',
          'error'
        );
      }
    };

  return (
    <section className="rounded-2xl border border-primary-200 bg-background-50 p-4 md:p-5 space-y-4">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground-900">
            Assistente local do BM Legal
          </h3>
          <p className="text-sm text-foreground-500 max-w-3xl">
            Envia a tarefa para o Local AI Engine. Quando cliente, caso ou procedimento forem informados, o Context Resolver acrescenta automaticamente o contexto estruturado antes do processamento.
          </p>
        </div>

        <button
          type="button"
          onClick={() => void loadJobs()}
          disabled={loading}
          className="px-3 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-60"
        >
          {loading ? 'Atualizando...' : 'Atualizar fila'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <label className="space-y-1">
          <span className="text-xs font-medium text-foreground-600">
            Operacao
          </span>
          <select
            value={task}
            onChange={(event) => setTask(event.target.value as LocalAITask)}
            className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm"
          >
            {(Object.keys(taskLabels) as LocalAITask[]).map((item) => (
              <option key={item} value={item}>
                {taskLabels[item]}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-foreground-600">
            Cliente ID
          </span>
          <input
            value={clientId}
            onChange={(event) => setClientId(event.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm"
            placeholder="Opcional"
          />
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-foreground-600">
            Caso ID
          </span>
          <input
            value={caseId}
            onChange={(event) => setCaseId(event.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm"
            placeholder="Opcional"
          />
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-foreground-600">
            Procedimento ID
          </span>
          <input
            value={procedureId}
            onChange={(event) => setProcedureId(event.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm"
            placeholder="Opcional"
          />
        </label>
      </div>

      <label className="space-y-1 block">
        <span className="text-xs font-medium text-foreground-600">
          O que o BM deve fazer?
        </span>
        <textarea
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-100 text-sm min-h-28"
          placeholder="Ex.: analise o contexto deste caso, identifique pendencias e prepare uma minuta de peticao para revisao."
        />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={submitting}
          className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60"
        >
          {submitting ? 'Enviando...' : 'Executar com IA local'}
        </button>

        <span className="text-xs text-foreground-500">
          Minutas sao classificadas como A3 e permanecem sujeitas a revisao humana.
        </span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-xl border border-background-200 overflow-hidden">
          <div className="px-3 py-2 bg-background-100 text-sm font-semibold text-foreground-800">
            Fila recente
          </div>

          <div className="divide-y divide-background-200">
            {jobs.length === 0 ? (
              <div className="p-3 text-sm text-foreground-500">
                Nenhuma tarefa registrada.
              </div>
            ) : (
              jobs.map((job) => (
                <button
                  key={job.id}
                  type="button"
                  onClick={() => void openJob(job.id)}
                  className="w-full text-left p-3 hover:bg-background-100"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-foreground-800">
                      {taskLabels[job.task] ?? job.task}
                    </span>
                    <span className="text-xs text-foreground-500">
                      {job.status}
                    </span>
                  </div>
                  <div className="text-xs text-foreground-500 mt-1 break-all">
                    {job.id}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="rounded-xl border border-background-200 overflow-hidden">
          <div className="px-3 py-2 bg-background-100 text-sm font-semibold text-foreground-800">
            Resultado
          </div>

          <div className="p-3">
            {!detail ? (
              <p className="text-sm text-foreground-500">
                Selecione uma tarefa para consultar o resultado persistido pelo Local AI Engine.
              </p>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3 text-xs text-foreground-500">
                  <span>{detail.status}</span>
                  <span>{detail.model ?? 'modelo local'}</span>
                </div>

                {detail.error ? (
                  <p className="text-sm text-red-700">
                    {detail.error}
                  </p>
                ) : (
                  <pre className="text-xs whitespace-pre-wrap rounded-lg bg-background-100 p-3 text-foreground-700 overflow-x-auto min-h-24">
                    {formatResult(detail.result) || 'Resultado ainda nao disponivel.'}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}